import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { UserCircle2, Save } from "lucide-react";

/**
 * My Profile — minimal self-service page.  Backend only persists `name`
 * for users, so we do not fabricate additional editable fields (per spec:
 * "Do not add unsupported database fields solely for visual purposes").
 */
export default function MyProfilePage() {
  const { user, refresh, activeMembership } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => { setName(user?.name || ""); }, [user?.name]);

  const dirty = name.trim() && name.trim() !== (user?.name || "");

  const save = async (e) => {
    e.preventDefault();
    if (!dirty) return;
    setSaving(true);
    try {
      await api.patch("/auth/me", { name: name.trim() });
      await refresh();
      toast.success("Profile updated");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error((typeof d === "object" && d?.message) || d || "Failed to update profile");
    } finally { setSaving(false); }
  };

  const role = user?.platform_role === "oneb_super_admin"
    ? "ONEB Super Admin"
    : (activeMembership?.role || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) || "Member";

  return (
    <div className="h-full w-full overflow-y-auto" data-testid="my-profile-page">
      <div className="max-w-[720px] mx-auto px-6 lg:px-10 py-8">
        <div className="mb-6">
          <div className="section-eyebrow flex items-center gap-2">
            <span className="section-num">.00</span>
            <span className="h-px w-6 bg-foreground/25" />
            Profile
          </div>
          <h1 className="mt-2 display-heading text-3xl ink leading-none tracking-tight">My Profile</h1>
          <p className="mt-2 text-[12.5px] ink-muted">
            Personal details you can update yourself. Roles and access are managed by your administrator.
          </p>
        </div>

        <form onSubmit={save} className="rounded-xl border hairline surface-card p-5 space-y-4">
          <div className="flex items-center gap-4 pb-4 border-b hairline-soft">
            <span className="h-14 w-14 grid place-items-center rounded-full bg-gradient-to-br from-primary to-secondary text-white text-[18px] font-semibold">
              {((user?.name || user?.email || "?").split(/\s+/).map((s) => s[0]).slice(0, 2).join("") || "?").toUpperCase()}
            </span>
            <div className="min-w-0">
              <div className="text-[15px] font-semibold ink truncate">{user?.name || "—"}</div>
              <div className="text-[11.5px] ink-muted truncate">{user?.email}</div>
              <div className="mt-1 text-[10.5px] ink-subtle">{role}</div>
            </div>
          </div>

          <label className="block">
            <div className="tiny-label">Full name</div>
            <input value={name} onChange={(e) => setName(e.target.value)}
              data-testid="profile-name-input" required minLength={2} maxLength={120}
              className="mt-1.5 h-10 w-full rounded-lg bg-background border hairline px-3 text-[13px] ink focus:outline-none focus:border-foreground/30" />
          </label>

          <div className="rounded-md bg-foreground/[0.04] p-3 text-[11.5px] ink-muted">
            Email address, role, and branch access can only be changed by an authorized administrator.
          </div>

          <div className="flex items-center justify-end gap-2 pt-1">
            <button type="submit" disabled={!dirty || saving}
              data-testid="profile-save"
              className="h-10 px-4 rounded-lg bg-foreground text-background text-[12.5px] font-semibold flex items-center gap-1.5 disabled:opacity-40">
              <Save size={13} /> {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
